import React from "react";
import Logo from "../components/Logo";
import ResultsContent from "../components/ResultsContent";
import ErrorBoundary from "../components/ErrorBoundary";
import ErrorFallback from "../components/ErrorFallback";
import ResultsFooter from "../components/ResultsFooter";
import { useSearchParams } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useEffect, useState } from "react";
import { Archetype } from "../types";
import { archetypes } from "../data/archetypes";

const ResultsPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const resultId = searchParams.get("id");
  const [result, setResult] = useState({
    primaryArchetype: {} as Archetype,
    secondaryArchetype: {} as Archetype,
    scores: {
      jaguar: 0,
      mariposa: 0,
      llama: 0,
      quetzal: 0,
      colibri: 0,
      coqui: 0,
      buho: 0,
      score_data: {},
      condor: 0, // Ensure condor is initialized
    },
    testResultId: "",
    userName: "",
    email: "",
    categoryScores: {},
  });
  const [loading, setLoading] = useState(true);

  // useEffect(() => {
  //   document.title = `I took the Test Latino™ and discovered I'm a ${result.primaryArchetype.name}`;

  //   const metaDescription = document.querySelector("meta[name='description']");
  //   if (metaDescription) {
  //     metaDescription.setAttribute("content", `I am a ${result.primaryArchetype.name} on the Latino Career Test ™. Discover what archetype you are by taking the test.`);
  //   } else {
  //     const meta = document.createElement("meta");
  //     meta.name = "description";
  //     meta.content = `I am a ${result.primaryArchetype.name} on the Latino Career Test ™. Discover what archetype you are by taking the test.`;
  //     document.head.appendChild(meta);
  //   }
  // }, [result]);

  useEffect(() => {
    const fetchResult = async () => {
      if (!resultId) return;
      const { data, error } = await supabase
        .from("test_results")
        .select("*")
        .eq("id", resultId)
        .single();

      const { data: userData, error: userError } = await supabase
        .from("users")
        .select("first_name, last_name, email")
        .eq("id", data.user_id)
        .single();

      console.log("Fetched Data:", data);
      console.log("Fetched User Data:", userData);

      if (error || userError) {
        console.error("Error fetching result:", error || userError);
        setLoading(false);
        return;
      }

      const finalData = {
        primaryArchetype:
          archetypes.find((a) => a.id === data.primary_archetype) ||
          ({} as Archetype), // fallback to empty Archetype object
        secondaryArchetype:
          archetypes.find((a) => a.id === data.secondary_archtype) ||
          ({} as Archetype),
        scores: {
          jaguar: data.jaguar_score,
          mariposa: data.condor_score || data.mariposa_score || 0,
          llama: data.llama_score,
          quetzal: data.quetzal_score,
          colibri: data.colibri_score,
          coqui: data.coqui_score,
          buho: data.buho_score,
          score_data: data.score_data || {},
          condor: data.condor_score || data.mariposa_score || 0, // Ensure condor score is set
        },
        testResultId: data.id, // use the real id, not crypto.randomUUID()
        userName: userData.first_name + " " + userData.last_name,
        email: userData.email ?? "",
        categoryScores: data.category_scores || {},
      };

      setResult(finalData);
      setLoading(false);
    };
    fetchResult();
  }, [resultId]);

  const onRestart = () => {
    // Reset state or navigate to questionnaire page
    setResult({
      primaryArchetype: {} as Archetype,
      secondaryArchetype: {} as Archetype,
      scores: {
        jaguar: 0,
        mariposa: 0,
        llama: 0,
        quetzal: 0,
        colibri: 0,
        coqui: 0,
        buho: 0,
        score_data: {},
        condor: 0, // Reset condor score
      },
      testResultId: "",
      userName: "",
      email: "",
      categoryScores: {},
    });
    setLoading(true);
  };

  if (loading) return <div className="text-center pt-6">Por favor espera mientras se genera el informe...</div>;
  if (!result) return <div>No se encontraron resultados.</div>;

  console.log("Fetched Result:", result);

  if (!result?.primaryArchetype) {
    return (
      <div className="min-h-screen bg-orange-50 flex items-center justify-center p-4">
        <ErrorFallback
          error={new Error("Resultados de pruebas no válidos")}
          resetErrorBoundary={onRestart}
          message="Error al cargar resultados"
        />
      </div>
    );
  }

  // console.log("Results Page Category Scores:", categoryScores);

  return (
    <>
      <div className="min-h-screen bg-orange-50 py-8">
        <div className="container mx-auto px-4 max-w-4xl">
          <header className="flex justify-center mb-8">
            <Logo size="medium" />
          </header>

          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              Tus Resultados del Test Latino™
            </h1>
            <p className="text-gray-600">
              Según tus respuestas, hemos identificado tu arquetipo del <b>Test Latino™</b>
            </p>
          </div>

          <ErrorBoundary
            fallback={
              <ErrorFallback
                error={new Error("Error al mostrar los resultados")}
                resetErrorBoundary={onRestart}
              />
            }
          >
            <ResultsContent result={result} onRestart={onRestart} />
          </ErrorBoundary>

          <ResultsFooter />
        </div>
      </div>
    </>
  );
};

export default ResultsPage;
